import { useEffect, useId, useRef, useState } from 'react';
import { Clock, Search as SearchIcon, X } from 'lucide-react';
import { formatTimeAgo } from '../../lib/timeAgo';
import type { RecentSearch } from './types';

const RECENT_SEARCH_DEBOUNCE_MS = 300;

interface RecentSearchInputProps {
  value: string;
  recentSearches: RecentSearch[];
  onChange: (value: string) => void;
  onRequestRecentSearches: (query?: string) => void;
  onSelect: (query: string) => void;
  inputClassName: string;
  iconClassName: string;
  showClearButton?: boolean;
}

export default function RecentSearchInput({
  value,
  recentSearches,
  onChange,
  onRequestRecentSearches,
  onSelect,
  inputClassName,
  iconClassName,
  showClearButton = false,
}: RecentSearchInputProps) {
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftQueryRef = useRef<string | null>(null);

  const showDropdown = isOpen && recentSearches.length > 0;
  const validActiveIndex =
    activeIndex >= 0 && activeIndex < recentSearches.length ? activeIndex : -1;

  const clearDebounce = () => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  const scheduleRequest = (query: string) => {
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      onRequestRecentSearches(query);
      debounceTimerRef.current = null;
    }, RECENT_SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => clearDebounce(), []);

  const requestSuggestions = () => {
    clearDebounce();
    onRequestRecentSearches(draftQueryRef.current === value ? value : '');
  };

  const selectSearch = (query: string) => {
    clearDebounce();
    draftQueryRef.current = null;
    setIsOpen(false);
    setActiveIndex(-1);
    onSelect(query);
  };

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          clearDebounce();
          setIsOpen(false);
          setActiveIndex(-1);
        }
      }}
    >
      <SearchIcon className={iconClassName} aria-hidden />
      <input
        type="text"
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          draftQueryRef.current = next;
          onChange(next);
          setIsOpen(true);
          setActiveIndex(-1);
          scheduleRequest(next);
        }}
        onFocus={() => {
          setIsOpen(true);
          setActiveIndex(-1);
          requestSuggestions();
        }}
        onClick={() => {
          setIsOpen(true);
          requestSuggestions();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            clearDebounce();
            setIsOpen(false);
            setActiveIndex(-1);
            return;
          }

          if (event.key === 'Enter' && !(showDropdown && validActiveIndex >= 0)) {
            clearDebounce();
            draftQueryRef.current = null;
            setIsOpen(false);
            setActiveIndex(-1);
            return;
          }

          if (!showDropdown) return;

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex(
              validActiveIndex < recentSearches.length - 1 ? validActiveIndex + 1 : 0
            );
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(
              validActiveIndex > 0 ? validActiveIndex - 1 : recentSearches.length - 1
            );
          } else if (event.key === 'Enter' && validActiveIndex >= 0) {
            event.preventDefault();
            selectSearch(recentSearches[validActiveIndex].query);
          }
        }}
        autoComplete="off"
        className={inputClassName}
        placeholder="Search records..."
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listboxId : undefined}
        aria-activedescendant={
          validActiveIndex >= 0 ? `${listboxId}-option-${validActiveIndex}` : undefined
        }
      />

      {showClearButton && value && (
        <button
          type="button"
          onClick={() => {
            draftQueryRef.current = '';
            onChange('');
            setIsOpen(true);
            setActiveIndex(-1);
            clearDebounce();
            onRequestRecentSearches('');
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" aria-hidden />
        </button>
      )}

      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Recent searches"
          className="absolute top-full z-50 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {recentSearches.map((search, index) => (
            <li key={search.id} role="none">
              <button
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={validActiveIndex === index}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectSearch(search.query)}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                  validActiveIndex === index
                    ? 'bg-emerald-50 text-emerald-900'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Clock className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{search.query}</span>
                <span className="shrink-0 text-xs text-gray-400">
                  {formatTimeAgo(search.date)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
